import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../../lib/api';
import type { CompletionPayload, LessonPayload } from '../../../lib/types';
import { Button, Card, Notice, Spinner } from '../../../ui';
import { QuestionCard } from '../QuestionCard';

interface Props {
  lesson: LessonPayload;
  onComplete: (result: CompletionPayload) => void;
}

/** One question per screen, then the server grades and closes the lesson. */
export function AssessmentStage({ lesson, onComplete }: Props) {
  const items = lesson.assessment.items;
  const [attemptId, setAttemptId] = useState(lesson.assessment.attemptId);
  const [index, setIndex] = useState(() => Math.max(0, items.findIndex((i) => !i.state.done)));
  const [finishedCount, setFinishedCount] = useState(() => items.filter((i) => i.state.done).length);
  const [error, setError] = useState('');
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (attemptId) return;
    api
      .post<{ attemptId: string }>(`/student/lessons/${lesson.lesson.id}/assessment/start`)
      .then((r) => setAttemptId(r.attemptId))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not start the quiz.'));
  }, [attemptId, lesson.lesson.id]);

  const complete = async () => {
    setClosing(true);
    setError('');
    try {
      onComplete(await api.post<CompletionPayload>(`/student/lessons/${lesson.lesson.id}/complete`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not finish the lesson. Try again.');
      setClosing(false);
    }
  };

  // Everything already answered (e.g. reload after the last question): just close.
  useEffect(() => {
    if (attemptId && finishedCount >= items.length && !closing) void complete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, finishedCount]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!attemptId) return <Spinner label="Getting your quiz ready" />;
  if (finishedCount >= items.length) return <Spinner label="Adding up your stars" />;

  const item = items[index]!;
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-muted">
          Question {index + 1} of {items.length}
        </p>
        <div className="flex gap-1.5" aria-hidden>
          {items.map((_, i) => (
            <span key={i} className={clsx('h-2.5 rounded-full transition-all', i < index ? 'w-6 bg-mint' : i === index ? 'w-10 bg-brand' : 'w-6 bg-line')} />
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={item.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        >
          <Card className="p-6 sm:p-9">
            <QuestionCard
              item={item}
              attemptId={attemptId}
              initial={item.state}
              continueLabel={index + 1 < items.length ? 'Next question' : 'Finish'}
              onFinished={() => {
                setFinishedCount((n) => n + 1);
                if (index + 1 < items.length) setIndex(index + 1);
              }}
            />
          </Card>
        </motion.div>
      </AnimatePresence>
      {closing && <Button loading disabled>Adding up your stars</Button>}
    </div>
  );
}
